<template>
    <li
        ref="tabItemRef"
        class="tab-item"
        v-bind:data-testid="`tab-item-${tab.value}`"
        v-bind:class="[
            tab.tabGA,
            { _active: isActive, _disabled: tab.disabled },
        ]"
        v-on:click="onTabItemClick"
    >
        {{ activeItem.title }}
        <div v-if="isNumber(activeItem.count) || tab.popoverList" class="tab-tip notranslate">
            <span v-if="isNumber(activeItem.count)" class="tab-count">
                {{ activeItem.count }}
            </span>
            <SvgIcon v-if="tab.popoverList" iconName="systemIcon_arrowBottom" />
        </div>
        <TabPopover
            v-if="tab.popoverList && isShowPopover"
            v-bind:value="activeTabValue"
            v-bind:list="tab.popoverList"
            v-bind:parentRef="tabItemRef"
            v-on:update="onPopoverUpdated"
        />
    </li>
</template>

<script lang="ts">
    import { onClickOutside } from '@vueuse/core';
    import isNumber from 'lodash/isNumber';
    import type { PropType } from 'vue';
    import { computed, defineComponent, ref } from 'vue';
    import TabPopover from '@/components/common/TabPopover.vue';
    import type { ITabItem } from '@/interface/Tab';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'TabItem',
        components: {
            TabPopover,
            SvgIcon,
        },
        emits: ['update:activeTabValue'],
        props: {
            tab: {
                type: Object as PropType<ITabItem>,
                required: true,
            },
            activeTabValue: {
                type: [String, Number],
                required: true,
            },
        },
        setup(props, context) {
            const tabItemRef = ref();

            const isShowPopover = ref(false);
            const activeItem = computed(() => {
                const target = props.tab.popoverList?.find(item => item.value === props.activeTabValue);
                if (target) return target;
                return props.tab;
            });

            const isActive = computed(() => activeItem.value.value === props.activeTabValue);

            const onTabItemClick = () => {
                if (props.tab.disabled) {
                    return;
                }
                if (props.tab.popoverList) {
                    isShowPopover.value = !isShowPopover.value;
                } else {
                    context.emit('update:activeTabValue', props.tab.value);
                }
            };

            const onPopoverUpdated = (value: string | number) => {
                isShowPopover.value = false;
                context.emit('update:activeTabValue', value);
            };

            onClickOutside(tabItemRef, () => {
                isShowPopover.value = false;
            });

            return {
                isNumber,
                activeItem,
                tabItemRef,
                isShowPopover,
                isActive,
                onTabItemClick,
                onPopoverUpdated,
            };
        },
    });
</script>
